import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import * as stylex from '@stylexjs/stylex';
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@VicOne-RD/miraidon-ui/command';
import {
  Dialog,
  DialogContent,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from '@VicOne-RD/miraidon-ui/dialog';
import { sentinel } from '../theme/semantic.stylex';
import { breakpoint } from '../theme/breakpoints.stylex';

export interface CommandNavItem {
  id: string;
  label: string;
  path: string;
  keywords?: string[];
  icon?: ReactNode;
}

export interface CommandEntityResultsContext {
  query: string;
  close: () => void;
}

/** Pre-resolved chrome strings. The app builds this with t('command-palette:…').
 *  @sentinel/ui has NO react-i18next dependency, so strings come in as props. */
export interface CommandPaletteLabels {
  triggerLabel: string;
  triggerHint: string;
  /** Accessible name for the trigger button. Kept distinct from inputAriaLabel
   *  so screen readers can tell the "open" button apart from the search field. */
  triggerAriaLabel: string;
  inputPlaceholder: string;
  inputAriaLabel: string;
  /** Visually-hidden <DialogTitle> text — the dialog's accessible name (a11y). */
  dialogTitle: string;
  groupNavigation: string;
  emptyDefault: string;
  noResults: (query: string) => string;
  footerNavigate: string;
  footerOpen: string;
  footerClose: string;
}

export interface CommandPaletteProviderProps {
  navItems: CommandNavItem[];
  onNavigate: (path: string) => void;
  renderEntityResults?: (ctx: CommandEntityResultsContext) => ReactNode;
  /** Arm the global ⌘K listener only when true (default true). The app passes
   *  enabled={!!user} so it never fires during the session probe. */
  enabled?: boolean;
  /** Localized chrome strings; English defaults fill any omitted key. */
  labels?: Partial<CommandPaletteLabels>;
  children: ReactNode;
}

export interface CommandPaletteContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  toggle: () => void;
}

/** English defaults keep the component standalone (the @sentinel/ui convention). */
const DEFAULT_LABELS: CommandPaletteLabels = {
  triggerLabel: 'Search',
  triggerHint: '⌘K',
  triggerAriaLabel: 'Open command palette',
  inputPlaceholder: 'Search pages, users, groups…',
  inputAriaLabel: 'Command palette',
  dialogTitle: 'Command palette',
  groupNavigation: 'Navigation',
  emptyDefault: 'Type to search pages and records',
  noResults: (query) => `No results for "${query}"`,
  footerNavigate: 'Navigate',
  footerOpen: 'Open',
  footerClose: 'Close',
};

const CommandPaletteContext = createContext<CommandPaletteContextValue | null>(null);
// Separate internal context for the resolved labels so the PUBLIC context value
// stays the locked { open, setOpen, toggle } shape. Read via useLabels() below.
const LabelsContext = createContext<CommandPaletteLabels>(DEFAULT_LABELS);

/** Case-insensitive substring match over label + keywords. Empty query matches all. */
export function matchNav(item: CommandNavItem, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (q === '') return true;
  if (item.label.toLowerCase().includes(q)) return true;
  return (item.keywords ?? []).some((k) => k.toLowerCase().includes(q));
}

/** True when focus sits in a field where ⌘K should type a literal "k" rather than toggle. */
function isTypingTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA') return true;
  return el.isContentEditable;
}

const styles = stylex.create({
  list: {
    maxHeight: '60vh',
    overflowY: 'auto',
    padding: '0.5rem',
  },
  // Visually-hidden (a11y): clip an element out of view while keeping it in the
  // accessibility tree — used for the DialogTitle that names the palette dialog.
  srOnly: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: 0,
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    borderWidth: 0,
  },
  // NOTE: the group heading is rendered + styled by miraidon's CommandGroup via its
  // `heading` prop, so no `groupHeading` style is defined here (it would be dead).
  // The heading color/uppercase already matches sentinel.textSubtle in miraidon's
  // command styles; if the holistic review (WU5) needs a tweak, add it then.
  item: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: '0.625rem',
    paddingTop: '0.5rem',
    paddingBottom: '0.5rem',
    paddingLeft: '0.875rem',
    paddingRight: '0.875rem',
    borderRadius: '0.5rem',
    color: sentinel.textBody,
    fontFamily: sentinel.fontBody,
    fontSize: '0.875rem',
    cursor: 'pointer',
    // cmdk sets data-selected on the active row; the brand-accent left bar is the
    // signature element, mirroring ops-shell's navAccent (semantic.stylex via ops-shell.tsx L163-171).
    backgroundColor: {
      default: 'transparent',
      ':hover': sentinel.stateHover,
    },
    transitionProperty: { default: 'background-color', '@media (prefers-reduced-motion: reduce)': 'none' },
    transitionDuration: sentinel.motionFast,
    transitionTimingFunction: sentinel.easeOut,
    // 2px brand-accent left bar (signature element), mirroring ops-shell's navAccent
    // (ops-shell.tsx L163-171). Shown on hover of the active row; WU5 refines the
    // keyboard-selected binding if needed.
    '::before': {
      content: '""',
      position: 'absolute',
      top: '0.375rem',
      bottom: '0.375rem',
      left: 0,
      width: '2px',
      borderRadius: '2px',
      backgroundColor: sentinel.brandPrimary,
      opacity: { default: 0, ':hover': 1 },
      transitionProperty: { default: 'opacity', '@media (prefers-reduced-motion: reduce)': 'none' },
      transitionDuration: sentinel.motionFast,
      transitionTimingFunction: sentinel.easeOut,
    },
  },
  itemLabel: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  itemIcon: {
    display: 'grid',
    placeItems: 'center',
    width: '1.125rem',
    height: '1.125rem',
    flexShrink: 0,
    color: 'currentColor',
  },
  empty: {
    padding: '1.5rem 0.875rem',
    textAlign: 'center',
    fontSize: '0.875rem',
    color: sentinel.textMuted,
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    paddingTop: '0.625rem',
    paddingBottom: '0.625rem',
    paddingLeft: '0.875rem',
    paddingRight: '0.875rem',
    borderTopWidth: '1px',
    borderTopStyle: 'solid',
    borderTopColor: sentinel.borderDefault,
    fontSize: '0.75rem',
    color: sentinel.textMuted,
  },
  footerHint: { display: 'flex', alignItems: 'center', gap: '0.375rem' },
  kbd: {
    fontFamily: sentinel.fontMono,
    fontSize: '0.6875rem',
    lineHeight: 1,
    padding: '0.1875rem 0.375rem',
    borderRadius: '0.25rem',
    backgroundColor: sentinel.surfaceOverlay,
    color: sentinel.textBody,
  },
  trigger: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
    paddingTop: '0.375rem',
    paddingBottom: '0.375rem',
    paddingLeft: '0.625rem',
    paddingRight: '0.5rem',
    borderRadius: '0.5rem',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: sentinel.borderDefault,
    backgroundColor: { default: 'transparent', ':hover': sentinel.stateHover },
    color: sentinel.textMuted,
    fontFamily: sentinel.fontBody,
    fontSize: '0.8125rem',
    cursor: 'pointer',
    transitionProperty: { default: 'background-color, color', '@media (prefers-reduced-motion: reduce)': 'none' },
    transitionDuration: sentinel.motionFast,
    transitionTimingFunction: sentinel.easeOut,
  },
  triggerGlyph: {
    display: 'grid',
    placeItems: 'center',
    width: '1rem',
    height: '1rem',
    flexShrink: 0,
    color: 'currentColor',
  },
  triggerLabel: { display: { default: 'inline', [breakpoint.narrow]: 'none' } },
  triggerHint: {
    display: { default: 'inline', [breakpoint.narrow]: 'none' },
    fontFamily: sentinel.fontMono,
    fontSize: '0.6875rem',
    padding: '0.125rem 0.375rem',
    borderRadius: '0.25rem',
    backgroundColor: sentinel.surfaceOverlay,
    color: sentinel.textMuted,
  },
});

function SearchGlyph() {
  return (
    <svg
      aria-hidden="true"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="7" cy="7" r="4.5" />
      <line x1="11" y1="11" x2="14" y2="14" />
    </svg>
  );
}

/** Path → nav-item testId fragment: drop the leading slash; "/" -> "dashboard". */
function navTestId(path: string): string {
  if (path === '/') return 'dashboard';
  return path.replace(/^\//, '');
}

export function CommandPaletteProvider({
  navItems,
  onNavigate,
  renderEntityResults,
  enabled,
  labels,
  children,
}: CommandPaletteProviderProps) {
  const [open, setOpen] = useState(false);
  const toggle = useCallback(() => setOpen((o) => !o), []);

  useEffect(() => {
    if (enabled === false) return;
    function onKey(e: KeyboardEvent) {
      const isToggleCombo = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k';
      if (!isToggleCombo) return;
      // Don't hijack the keystroke mid-IME-composition: while composing a CJK
      // character the browser still fires keydown for the committing key, and
      // toggling the palette there would destroy the in-progress input.
      if (e.isComposing) return;
      if (isTypingTarget(document.activeElement)) return;
      e.preventDefault();
      toggle();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enabled, toggle]);

  const ctx = useMemo<CommandPaletteContextValue>(
    () => ({ open, setOpen, toggle }),
    [open, toggle],
  );
  const resolvedLabels = useMemo<CommandPaletteLabels>(
    () => ({ ...DEFAULT_LABELS, ...labels }),
    [labels],
  );

  return (
    <CommandPaletteContext.Provider value={ctx}>
      <LabelsContext.Provider value={resolvedLabels}>
        {children}
        <CommandPalette
          navItems={navItems}
          onNavigate={onNavigate}
          {...(renderEntityResults && { renderEntityResults })}
          open={open}
          setOpen={setOpen}
        />
      </LabelsContext.Provider>
    </CommandPaletteContext.Provider>
  );
}

export function useCommandPalette(): CommandPaletteContextValue {
  const ctx = useContext(CommandPaletteContext);
  if (!ctx) throw new Error('useCommandPalette must be used within <CommandPaletteProvider>');
  return ctx;
}

export function CommandPaletteTrigger() {
  const labels = useLabels();
  const { toggle } = useCommandPalette();
  return (
    <button
      type="button"
      data-testid="command-palette-trigger"
      onClick={toggle}
      aria-label={labels.triggerAriaLabel}
      {...stylex.props(styles.trigger)}
    >
      <span {...stylex.props(styles.triggerGlyph)}>
        <SearchGlyph />
      </span>
      <span {...stylex.props(styles.triggerLabel)}>{labels.triggerLabel}</span>
      <kbd {...stylex.props(styles.triggerHint)}>{labels.triggerHint}</kbd>
    </button>
  );
}

/** Internal — resolved chrome labels (DEFAULT_LABELS outside a provider). */
function useLabels(): CommandPaletteLabels {
  return useContext(LabelsContext);
}

interface CommandPaletteDialogProps {
  navItems: CommandNavItem[];
  onNavigate: (path: string) => void;
  renderEntityResults?: (ctx: CommandEntityResultsContext) => ReactNode;
  open: boolean;
  setOpen: (open: boolean) => void;
}

function CommandPalette({
  navItems,
  onNavigate,
  renderEntityResults,
  open,
  setOpen,
}: CommandPaletteDialogProps) {
  const labels = useLabels();
  const [value, setValue] = useState('');
  const [debounced, setDebounced] = useState('');

  // Reset the query whenever the palette closes so it reopens clean.
  useEffect(() => {
    if (!open) {
      setValue('');
      setDebounced('');
    }
  }, [open]);

  // Debounce the query handed to the entity slot (250ms — matches SearchableSelect).
  useEffect(() => {
    const trimmed = value.trim();
    const id = setTimeout(() => setDebounced(trimmed), 250);
    return () => clearTimeout(id);
  }, [value]);

  const close = useCallback(() => setOpen(false), [setOpen]);

  const trimmed = value.trim();
  const matchedNav = navItems.filter((item) => matchNav(item, value));
  const hasEntitySlot = renderEntityResults != null;
  // Manual empty state: nothing matches AND no entity slot can fill the gap.
  const showEmpty = matchedNav.length === 0 && !hasEntitySlot;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogPortal>
        <DialogOverlay />
        <DialogContent>
          {/* Visually-hidden title gives the Radix dialog an accessible name
              (screen-reader announcement) and satisfies Radix's required
              DialogTitle. The palette intentionally has no visible header. */}
          <DialogTitle {...stylex.props(styles.srOnly)}>{labels.dialogTitle}</DialogTitle>
          <Command shouldFilter={false}>
            <div data-testid="command-palette">
              <CommandInput
                data-testid="command-palette-input"
                aria-label={labels.inputAriaLabel}
                placeholder={labels.inputPlaceholder}
                value={value}
                onValueChange={setValue}
                leftSlot={<SearchGlyph />}
              />
              <CommandList styles={styles.list}>
                {matchedNav.length > 0 ? (
                  <CommandGroup
                    heading={labels.groupNavigation}
                    data-testid="command-palette-group-navigation"
                  >
                    {matchedNav.map((item) => (
                      <CommandItem
                        key={item.id}
                        value={`nav:${item.path}`}
                        data-testid={`command-palette-item-nav-${navTestId(item.path)}`}
                        styles={styles.item as unknown as stylex.StyleXStyles}
                        onSelect={() => {
                          onNavigate(item.path);
                          close();
                        }}
                      >
                        {item.icon ? (
                          <span {...stylex.props(styles.itemIcon)}>{item.icon}</span>
                        ) : null}
                        <span {...stylex.props(styles.itemLabel)}>{item.label}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                ) : null}

                {hasEntitySlot && debounced !== ''
                  ? renderEntityResults({ query: debounced, close })
                  : null}

                {showEmpty ? (
                  <div data-testid="command-palette-empty" {...stylex.props(styles.empty)}>
                    {trimmed === '' ? labels.emptyDefault : labels.noResults(trimmed)}
                  </div>
                ) : null}
              </CommandList>
              <div {...stylex.props(styles.footer)}>
                <span {...stylex.props(styles.footerHint)}>
                  <kbd {...stylex.props(styles.kbd)}>↑↓</kbd>
                  {labels.footerNavigate}
                </span>
                <span {...stylex.props(styles.footerHint)}>
                  <kbd {...stylex.props(styles.kbd)}>↵</kbd>
                  {labels.footerOpen}
                </span>
                <span {...stylex.props(styles.footerHint)}>
                  <kbd {...stylex.props(styles.kbd)}>esc</kbd>
                  {labels.footerClose}
                </span>
              </div>
            </div>
          </Command>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}
